import { Check, ChevronDown, LoaderCircle, TriangleAlert } from 'lucide-react';
import type React from 'react';
import type { ReactNode } from 'react';
import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

/**
 * The small set of pieces every page is built from.
 *
 * The shapes are the ones Message and Security use — same tokens, same spacing, same names — so
 * that moving between the consoles of the suite never feels like moving between two products.
 * A console is read far more often than it is admired, and a table that behaves the same
 * everywhere is worth more than a component library.
 */

export function Page({
  eyebrow,
  title,
  description,
  actions,
  children,
}: {
  eyebrow?: ReactNode;
  title: string;
  description?: string;
  actions?: ReactNode;
  children: ReactNode;
}) {
  return (
    <>
      <header className="page-header">
        <div className="page-inner">
          <div className="page-title">
            {eyebrow ? <span className="eyebrow">{eyebrow}</span> : null}
            <h1>{title}</h1>
            {description ? <p>{description}</p> : null}
          </div>
          {actions ? <div className="page-actions">{actions}</div> : null}
        </div>
      </header>
      <div className="page-content fade-in">{children}</div>
    </>
  );
}

export function Card({ title, hint, children, className }: { title?: ReactNode; hint?: ReactNode; children: ReactNode; className?: string }) {
  return (
    <section className={`card${className ? ` ${className}` : ''}`}>
      {title ? (
        <div className="card-head">
          <div>
            <h2>{title}</h2>
            {hint ? <p>{hint}</p> : null}
          </div>
        </div>
      ) : null}
      {children}
    </section>
  );
}

export type Tone = 'neutral' | 'good' | 'warn' | 'bad' | 'info';

/** The tone of a stat block: the number itself carries the colour, not a decoration around it. */
const STAT_TONE: Record<Tone, string> = {
  neutral: 'neutral',
  good: 'success',
  warn: 'warning',
  bad: 'danger',
  info: 'neutral',
};

export function Stat({ label, value, hint, tone = 'neutral' }: { label: string; value: ReactNode; hint?: string; tone?: Tone }) {
  return (
    <div className={`stat stat-${STAT_TONE[tone]}`}>
      <div className="stat-label">
        <span>{label}</span>
      </div>
      <strong>{value}</strong>
      {hint ? <small>{hint}</small> : null}
    </div>
  );
}

const STATUS_TONE: Record<string, Tone> = {
  published: 'info',
  delivered: 'info',
  processed: 'good',
  retried: 'warn',
  failed: 'bad',
  'dead-lettered': 'bad',
};

export const toneForStatus = (status: string): Tone => STATUS_TONE[status] ?? 'neutral';

/** The class of the pill that says how a message ended, in the vocabulary of the other consoles. */
const BADGE_CLASS: Record<Tone, string> = {
  good: 'status status-delivered',
  bad: 'status status-failed',
  warn: 'status status-pending',
  info: 'status status-processing',
  neutral: 'pill pill-off',
};

export function Badge({ children, tone = 'neutral' }: { children: ReactNode; tone?: Tone }) {
  return (
    <span className={BADGE_CLASS[tone]}>
      {tone === 'neutral' ? null : <i />}
      {children}
    </span>
  );
}

export function Table({ head, children, empty }: { head: ReactNode; children: ReactNode; empty?: string }) {
  const rows = Array.isArray(children) ? children.flat() : [children];
  const isEmpty = rows.filter(Boolean).length === 0;
  return (
    <div className="table-wrap">
      <table>
        <thead>{head}</thead>
        <tbody>{children}</tbody>
      </table>
      {isEmpty && empty ? <Empty>{empty}</Empty> : null}
    </div>
  );
}

export function Field({ label, hint, children, plain }: { label: string; hint?: string; children: ReactNode; plain?: boolean }) {
  const inside = (
    <>
      <span className="field-label">{label}</span>
      {children}
      {hint ? <small className="field-hint">{hint}</small> : null}
    </>
  );
  // Un <label> che avvolge un pulsante gli rimanda sopra il clic: per la tendina nostra serve un div.
  return plain ? <div className="field">{inside}</div> : <label className="field">{inside}</label>;
}

const BUTTON_VARIANT = { default: 'secondary', primary: 'primary', ghost: 'ghost', danger: 'danger' } as const;

export function Button({
  children,
  onClick,
  variant = 'default',
  type = 'button',
  disabled,
}: {
  children: ReactNode;
  onClick?: () => void;
  variant?: 'default' | 'primary' | 'ghost' | 'danger';
  type?: 'button' | 'submit';
  disabled?: boolean;
}) {
  return (
    <button className={`btn btn-${BUTTON_VARIANT[variant]} btn-md`} type={type} onClick={onClick} disabled={disabled}>
      {children}
    </button>
  );
}

export function Loading({ what }: { what: string }) {
  return (
    <div className="loading">
      <LoaderCircle className="spin" size={16} />
      <span>Carico {what}…</span>
    </div>
  );
}

export function Problem({ error }: { error: unknown }) {
  const message = error instanceof Error ? error.message : 'Qualcosa non ha funzionato';
  return (
    <div className="error-banner" role="alert">
      <TriangleAlert size={16} />
      <div>
        <strong>{message}</strong>
      </div>
    </div>
  );
}

export function Empty({ children }: { children: ReactNode }) {
  return (
    <div className="empty">
      <p>{children}</p>
    </div>
  );
}

/** An id or a hash shown in full only when it matters: elsewhere the first characters are enough. */
export function Mono({ value, title }: { value: string; title?: string }) {
  return (
    <Tooltip label={title ?? value}>
      <code className="mono">{value}</code>
    </Tooltip>
  );
}

/**
 * La tendina della console: mai il <select> di sistema.
 *
 * Il menu nativo lo disegna il sistema operativo, con i suoi colori e il suo carattere: in una
 * console scura stona e, su Windows, arriva perfino bianco. Qui il controllo è nostro — stessi
 * token, stessa animazione delle altre tendine — e resta usabile da tastiera come quello vero:
 * frecce per scorrere, Invio per scegliere, Esc per chiudere, e il fuoco torna al pulsante.
 */
export interface DropdownOption {
  value: string;
  label: string;
}

export function Dropdown({
  value,
  options,
  onChange,
  placeholder = 'Seleziona',
  disabled,
  label,
}: {
  value: string;
  options: DropdownOption[];
  onChange: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
  label?: string;
}) {
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const root = useRef<HTMLDivElement>(null);
  const trigger = useRef<HTMLButtonElement>(null);

  const selected = options.find((option) => option.value === value);

  useEffect(() => {
    if (!open) return;
    const onPointer = (event: MouseEvent) => {
      if (!root.current?.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onPointer);
    return () => document.removeEventListener('mousedown', onPointer);
  }, [open]);

  const show = () => {
    if (disabled) return;
    setActive(Math.max(0, options.findIndex((option) => option.value === value)));
    setOpen(true);
  };

  const choose = (option: DropdownOption) => {
    onChange(option.value);
    setOpen(false);
    trigger.current?.focus();
  };

  const onKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Escape') {
      if (!open) return;
      event.preventDefault();
      setOpen(false);
      trigger.current?.focus();
      return;
    }
    if (!open) {
      if (event.key === 'ArrowDown' || event.key === 'ArrowUp' || event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        show();
      }
      return;
    }
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setActive((index) => (index + 1) % options.length);
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActive((index) => (index - 1 + options.length) % options.length);
    } else if (event.key === 'Home') {
      event.preventDefault();
      setActive(0);
    } else if (event.key === 'End') {
      event.preventDefault();
      setActive(options.length - 1);
    } else if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      const option = options[active];
      if (option) choose(option);
    } else if (event.key === 'Tab') {
      setOpen(false);
    }
  };

  return (
    <div className="dropdown" ref={root} onKeyDown={onKeyDown}>
      <button
        type="button"
        ref={trigger}
        className={`dropdown-trigger${open ? ' open' : ''}${selected ? '' : ' placeholder'}`}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={label}
        disabled={disabled}
        onClick={() => (open ? setOpen(false) : show())}
      >
        <span>{selected?.label ?? placeholder}</span>
        <ChevronDown size={16} />
      </button>
      {open ? (
        <ul className="dropdown-menu" role="listbox" aria-label={label}>
          {options.map((option, index) => (
            <li
              key={option.value}
              role="option"
              aria-selected={option.value === value}
              className={`${index === active ? 'active' : ''}${option.value === value ? ' selected' : ''}`}
              onMouseEnter={() => setActive(index)}
              onClick={() => choose(option)}
            >
              <span>{option.label}</span>
              {option.value === value ? <Check size={14} /> : null}
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}

/**
 * La spiegazione che compare accanto a un controllo: anche questa disegnata dalla console, non
 * dal sistema. Il `title` del browser arriva dopo un secondo buono, con il carattere del sistema
 * operativo, e da tastiera non compare affatto: qui appare subito, anche col fuoco sopra.
 *
 * La bolla la disegniamo in fondo alla pagina, non dentro al controllo. Il menu e le due barre
 * in alto sfocano quello che ci passa sotto, e un elemento che sfoca diventa il riferimento di
 * tutto ciò che ha dentro: la bolla restava prigioniera del menu e il suo bordo la tagliava a
 * metà. Fuori di lì sta sopra a tutto e non la taglia nessuno.
 */
export function Tooltip({ label, children, placement = 'top' }: { label: string; children: ReactNode; placement?: 'top' | 'bottom' | 'right' }) {
  const host = useRef<HTMLSpanElement>(null);
  const [at, setAt] = useState<{ top: number; left: number } | null>(null);

  // La bolla vive in coordinate di finestra, quindi uno scorrimento la lascerebbe indietro:
  // meglio farla sparire che vederla scivolare accanto a un'altra riga.
  useEffect(() => {
    if (at === null) return;
    const away = () => setAt(null);
    window.addEventListener('scroll', away, true);
    window.addEventListener('resize', away);
    return () => {
      window.removeEventListener('scroll', away, true);
      window.removeEventListener('resize', away);
    };
  }, [at]);

  const show = () => {
    const box = host.current?.getBoundingClientRect();
    if (!box) return;
    const clamp = (x: number) => Math.min(Math.max(x, 12), window.innerWidth - 12);
    if (placement === 'right') setAt({ top: box.top + box.height / 2, left: box.right });
    else if (placement === 'bottom') setAt({ top: box.bottom, left: clamp(box.left + box.width / 2) });
    else setAt({ top: box.top, left: clamp(box.left + box.width / 2) });
  };

  return (
    <span
      className={`tip tip-${placement}`}
      ref={host}
      onMouseEnter={show}
      onMouseLeave={() => setAt(null)}
      onFocus={show}
      onBlur={() => setAt(null)}
    >
      {children}
      {at
        ? createPortal(
            <span className={`tip-bubble at-${placement}`} role="tooltip" style={{ top: at.top, left: at.left }}>
              {label}
            </span>,
            document.body,
          )
        : null}
    </span>
  );
}
